import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye } from "@phosphor-icons/react";
import { AppLayout } from "../../components/layout/AppLayout";
import { LeverEditor } from "../../components/editor/LeverEditor";
import { DocxEditor } from "../../components/editor/DocxEditor";
import { EditorToolbar } from "../../components/editor/EditorToolbar";
import { DocxToolbar } from "../../components/editor/DocxToolbar";
import { StatusBar } from "../../components/editor/StatusBar";
import { SelectionToolbar } from "../../components/editor/SelectionToolbar";
import { BreadcrumbBar } from "../../components/navigation/BreadcrumbBar";
import { OutlineTree } from "../../components/navigation/OutlineTree";
import { PdfPreview } from "../../components/preview/PdfPreview";
import { MarkdownPreview } from "../../components/preview/MarkdownPreview";
import { ErrorList } from "../document/ErrorList";
import { useEditorStore } from "../../stores/editor";
import { useUIStore } from "../../stores/ui";
import {
  DEMO_TEMPLATES,
  getDemoTemplate,
  type DemoTemplate,
} from "../../lib/demoTemplates";
import { demoCompile, cleanupDemoCompile } from "../../lib/demoCompile";
import type { CompileResponse, CompileError } from "../../types/compilation";

import { DemoBanner } from "./panels/DemoBanner";
import { DemoFileTree } from "./panels/DemoFileTree";
import { DemoChatPanel } from "./panels/DemoChatPanel";
import { DemoCitationsPanel } from "./panels/DemoCitationsPanel";
import { DemoCommentsPanel } from "./panels/DemoCommentsPanel";
import { DemoContextPanel } from "./panels/DemoContextPanel";
import { DemoVersionsPanel } from "./panels/DemoVersionsPanel";
import { DemoVitalsPanel } from "./panels/DemoVitalsPanel";

const DEFAULT_TEMPLATE_ID = "paper";

export function DemoWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const templateIdParam = searchParams.get("template") || DEFAULT_TEMPLATE_ID;
  const initialTemplate = getDemoTemplate(templateIdParam) || DEMO_TEMPLATES[0];

  const [activeTemplate, setActiveTemplate] = useState<DemoTemplate>(initialTemplate);
  const [content, setContent] = useState(initialTemplate.content);
  const [compileResult, setCompileResult] = useState<CompileResponse | null>(null);
  const [compileErrors, setCompileErrors] = useState<CompileError[]>([]);
  const [isCompiling, setIsCompiling] = useState(false);

  const editorView = useEditorStore((s) => s.editorView);
  const cursorLine = useEditorStore((s) => s.cursorLine);
  const openBottomPanelTab = useUIStore((s) => s.openBottomPanelTab);

  const compileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      cleanupDemoCompile();
      if (compileTimerRef.current) clearTimeout(compileTimerRef.current);
    };
  }, []);

  const goSignIn = useCallback(() => navigate("/login"), [navigate]);

  const handleSelectTemplate = useCallback(
    (t: DemoTemplate) => {
      cleanupDemoCompile();
      setActiveTemplate(t);
      setContent(t.content);
      setCompileResult(null);
      setCompileErrors([]);
      setSearchParams({ template: t.id }, { replace: true });
    },
    [setSearchParams],
  );

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
  }, []);

  const handleCompile = useCallback(async () => {
    if (activeTemplate.doc_type !== "latex") return;
    setIsCompiling(true);
    setCompileErrors([]);
    try {
      const result = await demoCompile(content);
      setCompileResult(result);
      setCompileErrors([...result.errors, ...(result.warnings || [])]);
    } catch {
      setCompileErrors([
        { line: null, message: "Compilation request failed", severity: "error", file: null },
      ]);
    } finally {
      setIsCompiling(false);
    }
  }, [content, activeTemplate.doc_type]);

  const scrollToLine = useCallback(
    (line: number) => {
      if (!editorView) return;
      const clampedLine = Math.max(1, Math.min(line, editorView.state.doc.lines));
      const lineInfo = editorView.state.doc.line(clampedLine);
      editorView.dispatch({ selection: { anchor: lineInfo.from }, scrollIntoView: true });
      editorView.focus();
    },
    [editorView],
  );

  const handleDownloadSource = useCallback(() => {
    const ext =
      activeTemplate.doc_type === "latex"
        ? ".tex"
        : activeTemplate.doc_type === "markdown"
          ? ".md"
          : ".html";
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = activeTemplate.label.toLowerCase().replace(/\s+/g, "-") + ext;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [content, activeTemplate]);

  const handleDownloadPdf = useCallback(() => {
    const pdfUrl = compileResult?.pdf_url;
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = "document.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [compileResult]);

  const isDocx = activeTemplate.doc_type === "docx";
  const isLatex = activeTemplate.doc_type === "latex";
  const isMarkdown = activeTemplate.doc_type === "markdown";
  const docTypeForEditor = isMarkdown ? ("markdown" as const) : isLatex ? ("latex" as const) : ("docx" as const);
  const filename = activeTemplate.label + (isLatex ? ".tex" : isMarkdown ? ".md" : ".docx");

  const editorSlot = (
    <div className="relative h-full flex flex-col bg-[var(--bg-surface)]">
      <DemoBanner onSignIn={goSignIn} />
      {!isDocx && (
        <BreadcrumbBar
          content={content}
          docType={docTypeForEditor}
          currentLine={cursorLine}
          onNavigate={scrollToLine}
        />
      )}
      {isDocx ? <DocxToolbar /> : <EditorToolbar />}
      <div className="relative flex-1 min-h-0">
        {isDocx ? (
          <DocxEditor
            key={activeTemplate.id}
            value={content}
            onChange={handleContentChange}
          />
        ) : (
          <LeverEditor
            key={activeTemplate.id}
            value={content}
            onChange={handleContentChange}
            language={docTypeForEditor}
          />
        )}
        <SelectionToolbar onAction={() => {}} />
      </div>
      <StatusBar content={content} docType={docTypeForEditor} />
    </div>
  );

  const previewSlot = (
    <div className="flex flex-col h-full overflow-hidden bg-[var(--bg-primary)]">
      {isLatex && (
        <PdfPreview
          pdfUrl={compileResult?.pdf_url ?? null}
          isCompiling={isCompiling}
          compileErrors={compileErrors}
          onCompile={handleCompile}
        />
      )}
      {isMarkdown && <MarkdownPreview content={content} />}
      {isDocx && (
        <div className="flex flex-col items-center justify-center h-full gap-3 bg-[var(--bg-secondary)]">
          <Eye size={36} weight="thin" className="text-[var(--text-muted)]" />
          <p className="text-xs text-[var(--text-muted)]">
            WYSIWYG editor &mdash; preview is built into the editor
          </p>
        </div>
      )}
    </div>
  );

  return (
    <AppLayout
      documentTitle={filename}
      documentType={docTypeForEditor}
      isSaved={true}
      errorCount={compileErrors.filter((e) => e.severity === "error").length}
      onOpenHistory={() => openBottomPanelTab("versions")}
      onShare={goSignIn}
      onDownloadSource={handleDownloadSource}
      onDownloadPdf={isLatex ? handleDownloadPdf : undefined}
      onPrint={() => window.print()}
      onWordCount={() => openBottomPanelTab("summary")}
      editorSlot={editorSlot}
      previewSlot={previewSlot}
      hidePreview={isDocx}
      filesContent={
        <DemoFileTree activeId={activeTemplate.id} onSelect={handleSelectTemplate} />
      }
      outlineContent={
        <OutlineTree
          content={content}
          docType={docTypeForEditor}
          activeLine={cursorLine}
          onNavigate={scrollToLine}
        />
      }
      chatContent={<DemoChatPanel onSignIn={goSignIn} />}
      citationsContent={<DemoCitationsPanel onSignIn={goSignIn} />}
      commentsContent={<DemoCommentsPanel />}
      contextContent={<DemoContextPanel />}
      errorsContent={<ErrorList errors={compileErrors} onNavigate={scrollToLine} />}
      logsContent={
        compileResult?.log ? (
          <pre className="p-3 text-[11px] leading-relaxed font-mono text-[var(--text-secondary)] whitespace-pre-wrap max-h-[400px] overflow-auto">
            {compileResult.log}
          </pre>
        ) : (
          <div className="p-3 text-xs text-[var(--text-muted)]">
            No compilation logs yet. Compile your document to see output.
          </div>
        )
      }
      vitalsContent={<DemoVitalsPanel />}
      versionsContent={<DemoVersionsPanel onSignIn={goSignIn} />}
    />
  );
}
